import React, { FC, useRef, useState } from "react";
import { usePopper } from "react-popper";
import {
  Menu,
  MenuAction,
  MenuItem,
  NavbarMenuItem,
  NavbarMenuLink,
  Tooltip,
  TooltipOnHover,
} from "tombac";
import { GuideIcon } from "tombac-icons";
import useOutsideClick from "../../useOutsideClick";
import { useLegoland } from "../LegolandContext";
import { IconButton } from "./IconButton";
import { TrackingFunction } from "../navbar/LegolandNavbarTypes";

export interface GuideButtonProps {
  asNavbarItems?: boolean;
  manualUrl?: string;
  onGuidedTour?: () => void;
  track: TrackingFunction;
}

interface MenuItem {
  label: string;
  props: Record<string, any>;
}

const className = "MoveNavbar__guide-options";

const ButtonWithMenu: FC<{ items: MenuItem[] }> = ({ items }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null
  );
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [{ name: "offset", options: { offset: [5, 5] } }],
  });

  const ref = useRef<HTMLElement | null>(null);
  ref.current = referenceElement;
  useOutsideClick(ref, () => {
    if (isOpen) setIsOpen(false);
  });

  return (
    <Tooltip content="Guides" placement="bottom" size="s" variant="inverted">
      {(tooltip) => (
        <>
          <IconButton
            className={className}
            $ml="1sp"
            onClick={() => {
              setIsOpen(!isOpen);
              if (isOpen) {
                tooltip.close();
              }
            }}
            onMouseEnter={() => !isOpen && tooltip.open()}
            onMouseLeave={tooltip.close}
            ref={(el) => {
              setReferenceElement(el);
              tooltip.ref(el);
            }}
          >
            <GuideIcon />
          </IconButton>
          {isOpen && (
            <Menu
              ref={setPopperElement}
              style={styles.popper}
              {...attributes.popper}
            >
              {items.map((item) => (
                <MenuItem key={item.label}>
                  <MenuAction {...item.props}>{item.label}</MenuAction>
                </MenuItem>
              ))}
            </Menu>
          )}
        </>
      )}
    </Tooltip>
  );
};

export const GuideButton: FC<GuideButtonProps> = ({
  asNavbarItems = false,
  manualUrl,
  onGuidedTour,
  track,
}) => {
  const { Link } = useLegoland();

  const items: MenuItem[] = [];

  if (manualUrl) {
    items.push({
      label: "User guide",
      props: {
        as: Link,
        href: manualUrl,
        onClick: () => {
          track("page_click", {
            specificProps: {
              component_name: "header",
              component_sub_name: `user-guides-button`,
            },
          });
        },
        target: "_blank",
      },
    });
  }

  if (onGuidedTour) {
    items.push({
      label: "Guided tour",
      props: {
        onClick: () => {
          track("page_click", {
            specificProps: {
              component_name: "header",
              component_sub_name: `user-guides-tour-button`,
            },
          });
          onGuidedTour();
        },
      },
    });
  }

  if (asNavbarItems) {
    return (
      <>
        {items.map((item) => (
          <NavbarMenuItem key={item.label}>
            <NavbarMenuLink {...item.props}>{item.label}</NavbarMenuLink>
          </NavbarMenuItem>
        ))}
      </>
    );
  } else if (items.length === 1) {
    return (
      <TooltipOnHover
        content={items[0].label}
        placement="bottom"
        size="s"
        variant="inverted"
      >
        <IconButton className={className} {...items[0].props}>
          <GuideIcon />
        </IconButton>
      </TooltipOnHover>
    );
  } else if (items.length > 1) {
    return <ButtonWithMenu items={items} />;
  } else {
    return null;
  }
};
